/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  dataFetcher, endpoints, baseUrl, getToken,
} from 'Api';
import { AnimatedSettings } from 'Components/reactbits/AnimatedIcons';
import UsersPanel from './UsersPanel';
import MembershipUpgradeModal from './MembershipUpgradeModal';
import PlanUsersModal from './PlanUsersModal';
import BrandModal from './BrandModal';
import styles from './index.module.css';

const formatMoney = (cents) => `$${Math.round((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
// Exact, with cents — e.g. "$500.00".
const formatMoneyExact = (cents) => `$${((Number(cents) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Billing title → adverb for the "Renew …" line.
const cycleAdverb = (title) => {
  if (title === 'Annual') return 'Annually';
  if (title === 'Monthly') return 'Monthly';
  return title || 'Annually';
};
// Tier "coin" gradient by plan name (defaults to bronze).
const tierGradient = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('gold')) return 'radial-gradient(circle at 32% 28%, #f6de85, #d4af37 55%, #a9821f)';
  if (n.includes('silver')) return 'radial-gradient(circle at 32% 28%, #eef1f5, #c3ccd6 55%, #98a2ae)';
  if (n.includes('platinum') || n.includes('diamond')) return 'radial-gradient(circle at 32% 28%, #eaf3f7, #b9cfd8 55%, #8aa3ad)';
  return 'radial-gradient(circle at 32% 28%, #ecbe93, #cd7f32 55%, #9c5a1e)';
};
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Applied promo/coupon (membership.discount) → "Free", "40% off", "$20 off".
const discountValue = (d) => {
  if (!d) return '';
  if (d.percent_off) return d.percent_off === 100 ? 'Free' : `${d.percent_off}% off`;
  if (d.amount_off) return `${formatMoney(d.amount_off)} off`;
  return '';
};
const discountDuration = (d) => {
  if (!d) return '';
  if (d.duration === 'forever') return 'forever';
  if (d.duration === 'once') return 'one-time';
  if (d.duration === 'repeating') return d.duration_in_months ? `for ${d.duration_in_months} mo` : 'recurring';
  return '';
};

// A creation discount ({type,value}) → short label. 100% off reads as FREE.
const fmtDisc = (d) => {
  if (!d || !d.value) return '';
  if (d.type === 'percentage') return Number(d.value) === 100 ? 'Included' : `${d.value}% off`;
  return `$${(Number(d.value) / 100).toFixed(2)} off`;
};

// Badge as the (rounded) price the manager pays for a user on this plan after the
// discount, e.g. 25% off a $639 plan → "$479". 100% off → FREE. Falls back to the
// %/amount label until the plan's base price has loaded.
const badgeFor = (d, planAmount) => {
  if (!d || !d.value) return '';
  if (d.type === 'percentage' && Number(d.value) === 100) return 'Included';
  if (planAmount != null && Number.isFinite(planAmount)) {
    const eff = d.type === 'percentage'
      ? planAmount * (1 - Number(d.value) / 100)
      : planAmount - Number(d.value) / 100;
    return `$${Math.round(Math.max(0, eff)).toLocaleString('en-US')}`;
  }
  return fmtDisc(d);
};

// Member view — a two-column account dashboard:
//   left  = membership summary + Manage / Cancel / Resume
//   right = the full user-management UI (same as /manage-users)
// All three states are driven by my-membership (cancel_at_period_end toggles
// Cancel ↔ Resume; is_member:false is handled by the parent → plan grid).
// membershipState:
//   'active'  → normal member header (Manage / Cancel / Resume)
//   'none'    → active affiliate without a membership → "buy to create users"
//   'expired' → active affiliate with an ended membership → "reactivate"
// When not 'active', create-user is gated behind a popup that routes to plans
// (onGetMembership). Default 'active' keeps the legacy /user-management usage
// untouched.
const MemberView = ({
  membership, onChanged, affiliateActive = false, membershipState = 'active', onGetMembership,
  brandName = '', brandLogoUrl = '', brandLogoKey = '', brandFolder, onBrandSaved,
}) => {
  const [busy, setBusy] = useState(null); // 'manage' | 'cancel' | 'resume' | null
  const [brandOpen, setBrandOpen] = useState(false); // "Set your brand" popup
  const hasBrand = !!(brandName || brandLogoUrl);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false); // create-user membership gate
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // gear dropdown (manage / cancel / upgrade)
  const [gearHover, setGearHover] = useState(false);
  // Clicked plan card → { plan, origin } drives the grow-from-card users modal.
  const [planModal, setPlanModal] = useState(null);
  const openPlanUsers = (tile, e) => {
    const origin = e?.currentTarget?.getBoundingClientRect?.();
    setPlanModal({ plan: tile, origin });
  };

  // Cursor-following spotlight glow on the membership card (same effect as the
  // content-dashboard SpotlightCard).
  const cardRef = useRef(null);
  const handleCardMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--sc-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--sc-y', `${e.clientY - rect.top}px`);
  };
  // True only when a same-interval plan costs more than the current one; the
  // Upgrade button is hidden on the top plan.
  const [hasHigherPlan, setHasHigherPlan] = useState(false);

  const m = membership || {};
  const canceling = !!m.cancel_at_period_end; // active but scheduled to end
  const periodEnd = formatDate(m.current_period_end);

  // Per-cycle allowances + discounts → flatten each creation into offer tiles:
  //  • numeric `allowed` + `discount` → a capped free tier (bar, "X left").
  //  • `after_limit_discount` on a capped plan → a SEPARATE uncapped tile that
  //    kicks in once the free tier is used up (count = after_limit_used_this_cycle).
  //  • `allowed: null` + a discount → a single flat, uncapped discount tile.
  //  • no discount + no after-limit discount → not an offer, so it's dropped.
  const creations = Array.isArray(m.creations) ? m.creations : [];
  // Resolve creation plan_id → product name + base price (best-effort).
  const [planNames, setPlanNames] = useState({});
  const [planAmounts, setPlanAmounts] = useState({});
  const offerTiles = [];
  creations.forEach((c) => {
    const name = planNames[String(c.plan_id)] || `Plan ${c.plan_id}`;
    const amt = planAmounts[String(c.plan_id)];
    const capped = c.allowed !== null && c.allowed !== undefined;
    if (capped) {
      const allowed = Number(c.allowed) || 0;
      const used = Number(c.used_this_cycle) || 0;
      const exceeded = !!c.limit_exceeded || (allowed > 0 && used >= allowed);
      const remaining = c.free_remaining != null ? Number(c.free_remaining) : Math.max(0, allowed - used);
      if (c.discount) {
        offerTiles.push({
          key: `${c.plan_id}-free`,
          name,
          badge: badgeFor(c.discount, amt),
          count: `${used}/${allowed}`,
          pct: allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : 0,
          bar: true,
          used,
          allowed,
          exceeded,
          detail: exceeded ? 'Included used up' : `${remaining} left`,
        });
      }
      if (c.after_limit_discount) {
        offerTiles.push({
          key: `${c.plan_id}-after`,
          name,
          note: 'After Included',
          badge: badgeFor(c.after_limit_discount, amt),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.after_limit_used_this_cycle) || 0} created`,
        });
      }
    } else {
      const disc = c.discount || c.after_limit_discount;
      if (disc) {
        offerTiles.push({
          key: `${c.plan_id}-flat`,
          name,
          badge: badgeFor(disc, amt),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.used_this_cycle) || 0} created`,
        });
      }
    }
  });
  useEffect(() => {
    if (!creations.length) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await dataFetcher(endpoints.getAllActivePlans);
        if (active && res?.success && Array.isArray(res.data)) {
          const nameMap = {};
          const amtMap = {};
          res.data.forEach((p) => {
            nameMap[String(p.id)] = p.product_name;
            amtMap[String(p.id)] = Number(p.amount);
          });
          setPlanNames(nameMap);
          setPlanAmounts(amtMap);
        }
      } catch (e) { /* names/prices are best-effort */ }
    })();
    return () => { active = false; };
  }, [creations.length]);

  // Is there a higher plan to upgrade to? (Hidden on the top plan.)
  useEffect(() => {
    if (membershipState !== 'active') { setHasHigherPlan(false); return undefined; }
    const interval = m.billing_title || 'Annual';
    const curAmount = Number(m.amount_cents) || 0;
    let active = true;
    (async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${baseUrl}/v4/membership-plans`, token ? { params: { token } } : undefined);
        if (!active) return;
        const higher = Array.isArray(data?.data) && data.data.some((p) => {
          const pr = (p.pricing || []).find((x) => x.billing_title === interval);
          return pr && Number(pr.price_cents) > curAmount;
        });
        setHasHigherPlan(!!higher);
      } catch (e) { /* keep hidden on failure */ }
    })();
    return () => { active = false; };
  }, [membershipState, m.billing_title, m.amount_cents]);

  const membershipActive = membershipState === 'active';
  const expired = membershipState === 'expired';
  const gateText = expired
    ? 'Your membership has ended. Reactivate it to create new users.'
    : 'Become a member to create and manage new users.';
  const gateCta = expired ? 'Activate membership' : 'View membership plans';
  const handleGetMembership = () => { setGateOpen(false); onGetMembership?.(); };

  const openPortal = async () => {
    setBusy('manage');
    setErr('');
    try {
      const res = await dataFetcher('v4/membership/billing-portal', { return_url: window.location.href });
      if (res?.success && res?.data?.url) {
        window.location = res.data.url; // returns here later → hook re-fetches on mount
      } else {
        setErr(res?.message || 'Could not open the billing portal.');
        setBusy(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not open the billing portal.');
      setBusy(null);
    }
  };

  const doCancel = async () => {
    setBusy('cancel');
    setErr('');
    setMsg('');
    try {
      const res = await dataFetcher('v4/membership/cancel'); // graceful (at period end)
      if (res?.success) {
        setMsg(res.message || 'Your membership will end at the current period.');
        setConfirmOpen(false);
        onChanged?.(true); // silent reconcile → banner + Resume appear
      } else {
        setErr(res?.message || 'Could not cancel your membership.');
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not cancel your membership.');
    } finally {
      setBusy(null);
    }
  };

  const doResume = async () => {
    setBusy('resume');
    setErr('');
    setMsg('');
    try {
      const res = await dataFetcher('v4/membership/resume');
      if (res?.success) {
        setMsg(res.message || 'Membership resumed.');
        onChanged?.(true); // silent reconcile → banner clears, Cancel returns
      } else {
        setErr(res?.message || 'Could not resume your membership.');
      }
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not resume your membership.');
      if (e?.response?.status === 409) onChanged?.(true); // ended → becomes is_member:false
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={styles.member}>
      {/* ---- Not a member (active affiliate): buy / reactivate banner ---- */}
      {!membershipActive && (
        <div className={`${styles.gateBanner} ${expired ? styles.gateBannerWarn : ''}`}>
          <div className={styles.gateBannerBody}>
            <span className={styles.gateBannerTitle}>
              {expired ? 'Membership required' : 'Unlock user creation'}
            </span>
            <span className={styles.gateBannerText}>{gateText}</span>
          </div>
          <button type="button" className={styles.gateBannerBtn} onClick={onGetMembership}>
            {gateCta}
          </button>
        </div>
      )}

      {/* ---- Membership card: plan info + status/actions + this-cycle allowances ---- */}
      {membershipActive && (
      <div className={styles.membershipCard} ref={cardRef} onMouseMove={handleCardMouseMove}>
      <div className={styles.memberHeader}>
        <div className={styles.memberHeaderMain}>
          <div className={styles.memberTierRow}>
            <span
              className={styles.tierCoin}
              style={{ background: tierGradient(m.plan_name) }}
              aria-hidden="true"
            >
              {(m.plan_name || '?').charAt(0).toUpperCase()}
            </span>
            <span className={styles.tierName}>{m.plan_name}</span>
            <span className={`${styles.statusChip} ${canceling ? styles.statusChipEnding : styles.statusChipActive}`}>
              {canceling ? 'Ending' : (m.status || 'Active')}
            </span>
          </div>
        </div>

        <div className={styles.memberHeaderSide}>
          {/* Co-branding "Set your brand" entry — HIDDEN for now per request.
              Re-enable by uncommenting this block. */}
          {false && affiliateActive && (
            <button
              type="button"
              className={styles.brandChip}
              onClick={() => setBrandOpen(true)}
              title={hasBrand ? 'Edit your brand' : 'Set your brand'}
            >
              {brandLogoUrl ? (
                <img src={brandLogoUrl} alt="" className={styles.brandChipLogo} />
              ) : (
                <span className={styles.brandChipLogoEmpty} aria-hidden="true">
                  {(brandName || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <span className={styles.brandChipText}>
                <span className={styles.brandChipLabel}>Your brand</span>
                <span className={`${styles.brandChipName} ${hasBrand ? '' : styles.brandChipNameEmpty}`}>
                  {brandName || 'Set your brand'}
                </span>
              </span>
            </button>
          )}
          <div className={styles.gearWrap}>
            <button
              type="button"
              className={styles.gearBtn}
              onClick={() => setMenuOpen((v) => !v)}
              onMouseEnter={() => setGearHover(true)}
              onMouseLeave={() => setGearHover(false)}
              aria-label="Membership options"
              disabled={!!busy}
            >
              <AnimatedSettings size={20} animate={gearHover || menuOpen} />
            </button>
            {menuOpen && (
              <>
                <div className={styles.gearOverlay} onClick={() => setMenuOpen(false)} />
                <div className={styles.gearMenu} role="menu">
                  {m.discount && (
                    <div className={styles.gearPromo}>
                      <span className={styles.gearPromoIcon} aria-hidden="true">%</span>
                      <span className={styles.gearPromoName}>
                        {m.discount.name || m.discount.code || 'Promo applied'}
                      </span>
                      {discountValue(m.discount) && (
                        <span className={styles.gearPromoValue}>
                          {discountValue(m.discount)}
                          {discountDuration(m.discount) ? ` · ${discountDuration(m.discount)}` : ''}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Renewal, styled as the blue counterpart of the promo row
                      above: same shape, plan's accent colour. */}
                  {periodEnd && (
                    <div className={styles.gearRenew}>
                      <span className={styles.gearRenewIcon} aria-hidden="true">↻</span>
                      <span className={styles.gearRenewName}>
                        {canceling ? 'Ends on' : `Renews ${cycleAdverb(m.billing_title).toLowerCase()}`}
                      </span>
                      <span className={styles.gearRenewValue}>
                        {periodEnd}
                        {!canceling && m.amount_cents != null ? ` · ${formatMoneyExact(m.amount_cents)}` : ''}
                      </span>
                    </div>
                  )}
                  {m.can_upgrade && hasHigherPlan && (
                    <button
                      type="button"
                      className={styles.gearItem}
                      onClick={() => { setMenuOpen(false); setUpgradeOpen(true); }}
                    >
                      Upgrade plan
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.gearItem}
                    onClick={() => { setMenuOpen(false); openPortal(); }}
                    disabled={!!busy}
                  >
                    {busy === 'manage' ? 'Opening…' : 'Manage membership'}
                  </button>
                  {canceling ? (
                    <button
                      type="button"
                      className={styles.gearItem}
                      onClick={() => { setMenuOpen(false); doResume(); }}
                      disabled={!!busy}
                    >
                      {busy === 'resume' ? 'Resuming…' : 'Resume membership'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`${styles.gearItem} ${styles.gearItemDanger}`}
                      onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}
                      disabled={!!busy}
                    >
                      Cancel membership
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---- This-cycle allowances, inside the same card ---- */}
      {offerTiles.length > 0 && (
        <div className={styles.usage}>
          <span className={styles.usageLabel}>New users you can create this cycle</span>
          <div className={styles.usageList}>
            {offerTiles.map((t) => (
              <div
                key={t.key}
                className={styles.usageItem}
                role="button"
                tabIndex={0}
                title={`View users on ${t.name}`}
                onClick={(e) => openPlanUsers(t, e)}
                onKeyDown={(e) => { if (e.key === 'Enter') openPlanUsers(t, e); }}
              >
                <div className={styles.usageItemHead}>
                  <span className={styles.usageName}>{t.name}</span>
                  {t.badge ? (
                    <span className={`${styles.usageBadge} ${t.badge === 'Included' ? styles.usageBadgeFree : ''}`}>
                      {t.badge}
                    </span>
                  ) : null}
                </div>
                {/* Always render the note line so the detail/count line stays on
                    the same row across items with and without a note. */}
                <span className={styles.usageNote}>{t.note || ' '}</span>
                <div className={styles.usageItemMeta}>
                  <span className={styles.usageDetail}>{t.detail}</span>
                  <span className={`${styles.usageCount} ${t.exceeded ? styles.usageCountFull : ''}`}>{t.count}</span>
                </div>
                {t.bar && t.allowed > 0 && t.allowed <= 60 ? (
                  <span className={styles.usageGrid} aria-hidden="true">
                    {Array.from({ length: t.allowed }, (_, i) => (
                      <span
                        key={i}
                        className={`${styles.usageCell} ${i < t.used ? styles.usageCellOn : ''} ${t.exceeded && i < t.used ? styles.usageCellFull : ''}`}
                      />
                    ))}
                  </span>
                ) : t.bar ? (
                  <span className={styles.usageBar}>
                    <span
                      className={`${styles.usageBarFill} ${t.exceeded ? styles.usageBarFillFull : ''}`}
                      style={{ width: `${t.pct}%` }}
                    />
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
      )}

      {membershipActive && canceling && periodEnd && (
        <div className={styles.memberBanner}>{`Your membership ends on ${periodEnd}. You keep full access until then.`}</div>
      )}
      {msg && <div className={styles.memberOk}>{msg}</div>}
      {err && <p className={styles.coError}>{err}</p>}

      <div className={styles.memberDivider} />

      {/* ---- User management (V2-structured placement) ---- */}
      <UsersPanel
        blockCreateUser={affiliateActive && !membershipActive}
        onCreateUserBlocked={() => setGateOpen(true)}
      />

      <MembershipUpgradeModal
        open={upgradeOpen}
        membership={m}
        onClose={() => setUpgradeOpen(false)}
        onUpgraded={() => { setUpgradeOpen(false); onChanged?.(true); }}
      />

      <PlanUsersModal
        plan={planModal?.plan}
        origin={planModal?.origin}
        onClose={() => setPlanModal(null)}
      />

      <BrandModal
        open={brandOpen}
        onClose={() => setBrandOpen(false)}
        initialName={brandName}
        initialLogoUrl={brandLogoUrl}
        existingLogoKey={brandLogoKey}
        logoFolder={brandFolder}
        onSaved={onBrandSaved}
      />

      {gateOpen && (
        <div className={styles.modalOverlay} onClick={() => setGateOpen(false)}>
          <div
            className={styles.confirmCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>
                {expired ? 'Activate membership' : 'Membership required'}
              </h3>
              <p className={styles.confirmText}>
                {expired
                  ? 'You need an affiliate membership to create and access your users.'
                  : 'Creating new users is a member benefit. Choose a membership plan to get started.'}
              </p>
              <div className={styles.confirmActions}>
                <button type="button" className={styles.coSecondary} onClick={() => setGateOpen(false)}>
                  Not now
                </button>
                <button type="button" className={styles.gateModalBtn} onClick={handleGetMembership}>
                  {gateCta}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className={styles.modalOverlay} onClick={() => { if (!busy) setConfirmOpen(false); }}>
          <div
            className={styles.confirmCard}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalBody}>
              <h3 className={styles.confirmTitle}>Cancel membership?</h3>
              <p className={styles.confirmText}>
                {`Cancel your ${m.plan_name || ''} membership? You'll keep access until ${periodEnd || 'the end of the current period'}.`}
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.coSecondary}
                  onClick={() => setConfirmOpen(false)}
                  disabled={!!busy}
                >
                  Keep membership
                </button>
                <button
                  type="button"
                  className={styles.cancelConfirmBtn}
                  onClick={doCancel}
                  disabled={busy === 'cancel'}
                >
                  {busy === 'cancel' ? 'Canceling…' : 'Yes, cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberView;
